import {
  Button,
  Form,
  FormInstance,
  Input,
  Space,
  message,
} from "antd";
import React, { useEffect } from "react";
import {
  addClientAlternateContact,
  checkPhoneExists,
  fetchClientAlternateContact,
  fetchClientDetails,
} from "../../../apis/client.api";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";
interface AddClientAlternateContactFormProps {
  user_id: any;
  onClientAlternateContactDrawerClose: Function;
}

const AddClientAlternateContactForm = ({
  onClientAlternateContactDrawerClose,
  user_id,
}: AddClientAlternateContactFormProps) => {
  const [form] = Form.useForm();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission("client.update");

  const onFinish = async (updatedValues: any) => {
    if (!canEdit) {
      message.error("You don't have permission to add a contact");
      return;
    }
    const phone = updatedValues.contact_person_contact;
    const [existsInOrg, alternateContacts, client] = await Promise.all([
      checkPhoneExists(phone),
      fetchClientAlternateContact(user_id),
      fetchClientDetails(user_id),
    ]);
    if (String(client?.contact) === String(phone)) {
      message.error("Alternate contact cannot be the same as the client's contact");
      return;
    }
    if (existsInOrg) {
      message.error("This contact already exists in the organisation");
      return;
    }
    const existsAsAlternate = alternateContacts?.some(
      (c: any) => String(c?.contact_person_contact) === String(phone)
    );
    if (existsAsAlternate) {
      message.error("This contact is already added as an alternate contact");
      return;
    }
    addClientAlternateContact({ ...updatedValues }, user_id).then(() => {
      onClientAlternateContactDrawerClose();
    });
  };
  const SubmitButton = ({ form }: { form: FormInstance }) => {
    const [submittable, setSubmittable] = React.useState(false);

    // Watch all values
    const values = Form.useWatch([], form);

    React.useEffect(() => {
      form.validateFields({ validateOnly: true }).then(
        () => {
          setSubmittable(true);
        },
        () => {
          setSubmittable(false);
        }
      );
    }, [values]);

    useEffect(() => {
      form.resetFields();
    }, []);

    return (
      <Button
        type="primary"
        htmlType="submit"
        disabled={!submittable || !canEdit}
      >
        Submit
      </Button>
    );
  };


  return (
    <Form
      form={form}
      name="validateOnly"
      layout="vertical"
      autoComplete="off"
      onFinish={onFinish}
    >
      <Form.Item
        name="contact_person_name"
        rules={[
          {
            required: true,
          },
        ]}
        label="Contact person name"
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="contact_person_contact"
        rules={[
          {
            required: true,
            pattern: /^[6-9]\d{9}$/gi,
            message: "Enter a valid Phone",
          },
          {
            validator: async (_, value) => {
              if (!value || value.length !== 10) return Promise.resolve();
              const [existsInOrg, alternateContacts, client] = await Promise.all([
                checkPhoneExists(value),
                fetchClientAlternateContact(user_id),
                fetchClientDetails(user_id),
              ]);
              if (String(client?.contact) === String(value)) {
                return Promise.reject(
                  "Alternate contact cannot be the same as the client's contact"
                );
              }
              if (existsInOrg) {
                return Promise.reject(
                  "This contact already exists in the organisation"
                );
              }
              const existsAsAlternate = alternateContacts?.some(
                (c: any) => String(c?.contact_person_contact) === String(value)
              );
              if (existsAsAlternate) {
                return Promise.reject(
                  "This contact is already added as an alternate contact"
                );
              }
              return Promise.resolve();
            },
          },
        ]}
        label="Contact person phone"
      >
        <Input />
      </Form.Item>
      <Form.Item name="contact_person_email" label="Contact person email" rules={[
        {
          type: "email",
          message: "Enter a valid email",
        }
      ]}>
        <Input />
      </Form.Item>

      <Form.Item>
        <Space>
          <SubmitButton form={form} />
          {/* <Button htmlType="reset">Reset</Button> */}
        </Space>
      </Form.Item>
    </Form>
  );
};

export default AddClientAlternateContactForm;
